import { useEffect, useState, useRef, useMemo, useCallback, type ChangeEvent } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { PulseLoader } from "react-spinners"
import {
    Button,
    Dialog as MuiDialog,
    DialogActions,
    DialogContent as MuiDialogContent,
    DialogContentText,
    DialogTitle as MuiDialogTitle,
} from "@mui/material"
import EditComponent, { type CollectionWizardChrome } from "./EditComponent"
import { IComponent } from "../../definitions/componentDef"
import { ActionTypes } from "@/actions"
import { useDispatch, useSelector } from "react-redux"
import { useAuth } from "../auth/AuthContext"
import { usePermission } from "../acl/usePermission"
import { toast } from "react-toastify"
import { RefreshCw, Plus, X, Database, ExternalLink, Download, Upload, Search } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { searchInputOverlayIconClass } from "@/lib/searchInputIcon"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button as ShadcnButton } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { buildCollectionPath } from "../utils/collectionPaths"
import { EMPTY_ARRAY } from "@/constants/reduxStableRefs"
import { Checkbox } from "@/components/ui/checkbox"
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu"
import { dataAPI } from "../api/dataApi"
import {
    buildCollectionExportBlob,
    downloadBlob,
    findMatchingComponent,
    findOtherComponentWithMongoCollection,
    findOtherComponentWithSlugOrganization,
    getMongoCollectionKey,
    getSlugOrganizationKey,
    mergeImportedForUpdate,
    parseCollectionConfigImportData,
    stripComponentForCreate,
} from "./collectionConfigTransfer"

function getQuickSetFields(fields: any[]) {
    return (
        fields?.filter(
            (f) =>
                f?.quickSetValue !== undefined &&
                f?.quickSetValue !== null &&
                String(f.quickSetValue).trim() !== ""
        ) || []
    )
}

const Components = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const [showNewDrawer, setShowNewDrawer] = useState(false)
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [componentToDelete, setComponentToDelete] = useState<any>(null)
    const [deleteInProgress, setDeleteInProgress] = useState(false)
    const [showCancelConfirm, setShowCancelConfirm] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [selectedUids, setSelectedUids] = useState<Set<string>>(() => new Set())
    const [importBusy, setImportBusy] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const importFileRef = useRef<HTMLInputElement>(null)

    const componentToEdit = useSelector((state: any) => state.component.current)
    const reduxUser = useSelector((state: any) => state.user)
    const isNew = useSelector((state: any) => state.component.isNew)
    const { user: authUser, refreshUser } = useAuth()
    const editComponentRef = useRef<{
        save: () => Promise<boolean>
        hasInteracted: () => boolean
        wizardBack?: () => void
    }>(null)
    const [newEditorNonce, setNewEditorNonce] = useState(0)
    const [collectionWizardUi, setCollectionWizardUi] = useState<CollectionWizardChrome | null>(null)
    const allowCloseWithoutConfirmRef = useRef(false)
    const user = authUser || reduxUser
    const userId = useMemo(
        () =>
            user?.uid ||
            user?.info?.uid ||
            user?.info?._id ||
            user?.info?.id ||
            user?.id ||
            user?._id ||
            null,
        [user]
    )

    const components = useSelector((state: any) => state.components.data ?? EMPTY_ARRAY)
    const componentsLoading = useSelector((state: any) => state.components.loading) || false
    const isAdmin = Boolean(user?.admin ?? user?.info?.admin)
    const viewAdvancedRequest = useMemo(
        () => ({ action: "view_collection_advanced" as const, targetType: "collection" as const, targetId: "*" }),
        []
    )
    const { allowed: canViewCollectionAdvanced } = usePermission(viewAdvancedRequest, { enabled: Boolean(userId) })
    const limit = user?.collectionLimit ?? user?.info?.collectionLimit
    const count = user?.collectionCount ?? user?.info?.collectionCount ?? 0
    const hasNoLimit = limit == null
    const atLimit = typeof limit === 'number' && limit > 0 && count >= limit
    const canCreateNew = isAdmin || (!hasNoLimit && !atLimit)

    const cleanupOverlays = () => {
        // Clean up all possible overlay elements that might block interactions
        const overlaySelectors = [
            '[data-radix-dialog-overlay]',
            '[data-radix-alert-dialog-overlay]'
        ];

        overlaySelectors.forEach(selector => {
            const overlays = document.querySelectorAll(selector);
            overlays.forEach((overlay) => {
                const element = overlay as HTMLElement;
                const state = element.getAttribute('data-state');
                const ariaHidden = element.getAttribute('aria-hidden');

                // Only cleanup if explicitly closed or hidden
                if (state === 'closed' || ariaHidden === 'true') {
                    element.style.display = 'none';
                    element.style.pointerEvents = 'none';
                    element.style.visibility = 'hidden';
                    element.style.opacity = '0';
                }
            });
        });

        // Also check for any fixed elements with high z-index that might be overlays
        // Look for Radix UI overlay patterns
        const allFixedElements = Array.from(document.querySelectorAll('*')).filter((el) => {
            const style = window.getComputedStyle(el);
            return style.position === 'fixed' &&
                (el.hasAttribute('data-radix-dialog-overlay') ||
                    el.hasAttribute('data-radix-alert-dialog-overlay'));
        });

        allFixedElements.forEach((element) => {
            const el = element as HTMLElement;
            const state = el.getAttribute('data-state');
            const ariaHidden = el.getAttribute('aria-hidden');

            // If it's a closed overlay, ensure it doesn't block interactions
            if (state === 'closed' || ariaHidden === 'true') {
                el.style.display = 'none';
                el.style.pointerEvents = 'none';
                el.style.visibility = 'hidden';
                el.style.opacity = '0';
            }
        });
    }

    useEffect(() => {
        if (userId && user) {
            dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user } })
        }
    }, [userId, dispatch, user])

    useEffect(() => {
        if (isRefreshing && !componentsLoading) {
            setIsRefreshing(false)
        }
    }, [componentsLoading, isRefreshing])

    useEffect(() => {
        if (location.state?.create !== true || !userId) return
        const limit = user?.collectionLimit ?? user?.info?.collectionLimit
        const count = user?.collectionCount ?? user?.info?.collectionCount ?? 0
        const hasNoLimit = limit == null
        const atLimit = typeof limit === 'number' && limit > 0 && count >= limit
        if (!isAdmin && (hasNoLimit || atLimit)) {
            const message = hasNoLimit
                ? 'You cannot create collections yet. Please wait for an administrator to give you access.'
                : 'You cannot create more collections. Please wait for an administrator to increase your limit or grant access.'
            toast.warn(message, { autoClose: 8000 })
            navigate(location.pathname, { replace: true, state: {} })
            return
        }
        setNewEditorNonce((n) => n + 1)
        dispatch({ type: ActionTypes.EDIT_COMPONENT_NEW, payload: { userId } })
        setShowNewDrawer(true)
        navigate(location.pathname, { replace: true, state: {} })
    }, [location.state?.create, location.pathname, userId, dispatch, navigate, user, isAdmin])

    useEffect(() => {
        const editUid = (location.state as any)?.editUid
        if (!editUid || !components.length) return
        const component = components.find((c: any) => c.uid === editUid)
        if (component) {
            dispatch({ type: ActionTypes.EDIT_COMPONENT_START, payload: component })
            setShowNewDrawer(true)
            navigate(location.pathname, { replace: true, state: {} })
        }
    }, [location.state, components, dispatch, navigate, location.pathname])

    // Cleanup overlays when dialogs close
    useEffect(() => {
        if (!showNewDrawer && !showCancelConfirm) {
            // Cleanup after a short delay to ensure animations complete
            const timer = setTimeout(() => {
                cleanupOverlays();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [showNewDrawer, showCancelConfirm])

    const handleOpenCollection = (component: { slug?: string; organization?: string | null }) => {
        const s = typeof component.slug === "string" ? component.slug.replace(/[!]+$/, "").trim() : ""
        if (!s) return
        navigate(buildCollectionPath({ slug: s, organization: component.organization }))
    }

    const handleCloseDeleteDialog = () => {
        if (!deleteInProgress) {
            setOpenDeleteDialog(false)
            setComponentToDelete(null)
        }
    }

    const handleNewComponent = () => {
        let uid = authUser?.uid ?? reduxUser?.uid ?? null
        if (!uid) {
            console.error('User info not available:', { authUser, reduxUser })
            alert('User information not loaded. Please try again.')
            return
        }
        const limit = user?.collectionLimit ?? user?.info?.collectionLimit
        const count = user?.collectionCount ?? user?.info?.collectionCount ?? 0
        const hasNoLimit = limit == null
        const atLimit = typeof limit === 'number' && limit > 0 && count >= limit
        if (!isAdmin && (hasNoLimit || atLimit)) {
            const message = hasNoLimit
                ? 'You cannot create collections yet. Please wait for an administrator to give you access.'
                : 'You cannot create more collections. Please wait for an administrator to increase your limit or grant access.'
            toast.warn(message, { autoClose: 8000 })
            return
        }
        setNewEditorNonce((n) => n + 1)
        dispatch({ type: ActionTypes.EDIT_COMPONENT_NEW, payload: { userId: uid } })
        setShowNewDrawer(true)
    }

    const handleConfirmDelete = async () => {
        if (!componentToDelete?.uid) return
        setDeleteInProgress(true)
        dispatch({ type: ActionTypes.COMPONENT_DELETE, payload: componentToDelete.uid })
        setOpenDeleteDialog(false)
        setComponentToDelete(null)
        setDeleteInProgress(false)
    }

    const handleClickEditComponent = (component: IComponent) => {
        const componentToUse = componentToEdit.uid === component.uid ? componentToEdit : component
        dispatch({ type: ActionTypes.EDIT_COMPONENT_START, payload: componentToUse })
        setShowNewDrawer(true)
    }

    const handleDeleteClick = (component: any) => {
        const slug = (component?.slug || "").toLowerCase()
        if (slug === "users") {
            toast.error("The Users collection cannot be deleted.")
            return
        }
        setComponentToDelete(component)
        setOpenDeleteDialog(true)
    }

    const handleOnEditComponent = () => {
        allowCloseWithoutConfirmRef.current = true
        setCollectionWizardUi(null)
        setShowNewDrawer(false)
        setShowCancelConfirm(false)
        setTimeout(() => {
            cleanupOverlays()
        }, 50)
        void refreshList()
        void refreshUser?.()
    }

    const handleCancelClick = () => {
        if (editComponentRef.current?.hasInteracted()) {
            setShowCancelConfirm(true)
        } else {
            setCollectionWizardUi(null)
            setShowNewDrawer(false)
            // Cleanup overlays when dialog closes without confirmation
            setTimeout(() => {
                cleanupOverlays();
            }, 50);
        }
    }

    const handleConfirmCancel = () => {
        setShowCancelConfirm(false)
        // Reset component to backup if it exists (only for existing components, not new ones)
        if (!isNew) {
            dispatch({ type: ActionTypes.EDIT_COMPONENT_RESET })
        }
        setCollectionWizardUi(null)
        setShowNewDrawer(false)

        // Force cleanup of any remaining overlays that might block clicks
        setTimeout(() => {
            cleanupOverlays();
        }, 50);

        // Also cleanup after animation completes
        setTimeout(() => {
            cleanupOverlays();
        }, 300);
    }

    const refreshList = async (force: boolean = false): Promise<void> => {
        if (!user) return
        setIsRefreshing(true)
        dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user, force } })
        await refreshUser?.()
    }

    const handleSavedOrCreated = async (
        createdComponent?: { slug?: string; uid?: string; organization?: string | null;[key: string]: any }
    ) => {
        allowCloseWithoutConfirmRef.current = true
        setCollectionWizardUi(null)
        setShowNewDrawer(false)
        setTimeout(() => cleanupOverlays(), 50)
        await refreshList(true)
        await refreshUser?.()
        if (createdComponent?.slug) {
            const s = String(createdComponent.slug).replace(/[!]+$/, "").trim()
            if (s) {
                navigate(buildCollectionPath({ slug: s, organization: createdComponent.organization }))
            }
        }
    }

    /** EditComponent invokes this before onEditComponent; do not close the drawer here (parent handles it). */
    const handleNewComponentCreated = async (created?: { slug?: string; organization?: string | null }) => {
        await refreshList(true)
        await refreshUser?.()
        if (created?.slug) {
            const s = String(created.slug).replace(/[!]+$/, "").trim()
            if (s) {
                navigate(buildCollectionPath({ slug: s, organization: created.organization }))
            }
        }
    }

    const handleReloadComponents = () => {
        if (!user) return
        void refreshList(true)
    }

    const selectedComponents = useMemo(
        () => (components as any[]).filter((c) => selectedUids.has(c.uid)),
        [components, selectedUids]
    )

    const filteredComponents = useMemo(() => {
        const q = searchQuery.trim().toLowerCase()
        if (!q) return components as any[]
        return (components as any[]).filter((c) => {
            const haystack = [
                c?.name,
                c?.slug,
                c?.uid,
                c?.creatorDisplayName,
                c?.creatorEmail,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
            return haystack.includes(q)
        })
    }, [components, searchQuery])

    const allRowsSelected =
        filteredComponents.length > 0 && filteredComponents.every((c) => selectedUids.has(c.uid))
    const someRowsSelected = selectedUids.size > 0

    const toggleSelectUid = (uid: string, checked: boolean) => {
        setSelectedUids((prev) => {
            const next = new Set(prev)
            if (checked) next.add(uid)
            else next.delete(uid)
            return next
        })
    }

    const toggleSelectAll = () => {
        if (allRowsSelected) {
            setSelectedUids(new Set())
        } else {
            setSelectedUids(new Set(filteredComponents.map((c) => c.uid)))
        }
    }

    const clearSelection = () => setSelectedUids(new Set())

    const exportConfigsToFile = (list: any[], filename: string) => {
        if (!list.length) {
            toast.warn("No collections selected.")
            return
        }
        downloadBlob(buildCollectionExportBlob(list), filename)
    }

    const handleExportSelected = () => {
        exportConfigsToFile(selectedComponents, `collections-export-${selectedComponents.length}.json`)
    }

    const handleExportAll = () => {
        exportConfigsToFile(components as any[], "collections-export-all.json")
    }

    const handleExportOne = (component: any) => {
        const slug = String(component?.slug || "config").replace(/[^a-z0-9_-]/gi, "_")
        exportConfigsToFile([component], `collection-${slug}.json`)
    }

    const handleImportFile = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0]
            e.target.value = ""
            if (!file || !user) return
            setImportBusy(true)
            try {
                const text = await file.text()
                let parsed: unknown
                try {
                    parsed = JSON.parse(text)
                } catch {
                    toast.error("Invalid JSON file.")
                    return
                }
                const items = parseCollectionConfigImportData(parsed)
                if (!items.length) {
                    toast.error("No collection configurations found in file.")
                    return
                }
                let created = 0
                let updated = 0
                let skipped = 0
                const errors: string[] = []
                const mongoKeyOwnerInBatch = new Map<string, string>()
                const slugOrgOwnerInBatch = new Map<string, string>()
                for (const raw of items) {
                    const comp = raw as Record<string, unknown>
                    const label = String(comp.name || comp.slug || "entry")
                    const slug = String(comp.slug || "")
                        .trim()
                        .toLowerCase()
                        .replace(/[!]+$/, "")
                    if (slug === "users") {
                        skipped++
                        continue
                    }
                    if (!comp.slug || !Array.isArray(comp.fields)) {
                        errors.push(`${label}: missing slug or fields`)
                        continue
                    }
                    const match = findMatchingComponent(components as any[], comp)
                    const excludeUid = match ? String((match as any).uid) : null
                    const mongoKey = getMongoCollectionKey(comp)
                    if (mongoKey) {
                        if (mongoKeyOwnerInBatch.has(mongoKey)) {
                            const priorUid = mongoKeyOwnerInBatch.get(mongoKey)!
                            if (excludeUid !== priorUid) {
                                errors.push(
                                    `${label}: mongoCollection "${mongoKey}" is already used in this import batch`
                                )
                                continue
                            }
                        }
                        const conflict = findOtherComponentWithMongoCollection(
                            components as any[],
                            mongoKey,
                            excludeUid
                        )
                        if (conflict) {
                            const other = conflict as Record<string, unknown>
                            const otherLabel = String(other.name || other.slug || "another collection")
                            errors.push(
                                `${label}: mongoCollection "${mongoKey}" is already used by "${otherLabel}"`
                            )
                            continue
                        }
                    }
                    const slugOrgKey = getSlugOrganizationKey(comp)
                    if (slugOrgOwnerInBatch.has(slugOrgKey)) {
                        const priorUid = slugOrgOwnerInBatch.get(slugOrgKey)!
                        if (excludeUid !== priorUid) {
                            errors.push(
                                `${label}: slug and organization already used in this import batch`
                            )
                            continue
                        }
                    }
                    const slugConflict = findOtherComponentWithSlugOrganization(
                        components as any[],
                        comp,
                        excludeUid
                    )
                    if (slugConflict) {
                        const other = slugConflict as Record<string, unknown>
                        const otherLabel = String(other.name || other.slug || "another collection")
                        errors.push(
                            `${label}: slug/organization already used by "${otherLabel}"`
                        )
                        continue
                    }
                    try {
                        let res: unknown
                        if (match) {
                            const merged = mergeImportedForUpdate(comp, match as Record<string, unknown>)
                            res = await dataAPI.updateComponent(String((match as any).uid), merged)
                            updated++
                        } else {
                            res = await dataAPI.createComponent(stripComponentForCreate(comp))
                            created++
                        }
                        const r = res as { component?: { uid?: string }; data?: { uid?: string } }
                        const uidForBatch = match
                            ? String((match as any).uid)
                            : String(r?.component?.uid || r?.data?.uid || "")
                        if (uidForBatch) {
                            if (mongoKey) mongoKeyOwnerInBatch.set(mongoKey, uidForBatch)
                            slugOrgOwnerInBatch.set(slugOrgKey, uidForBatch)
                        }
                    } catch (err) {
                        errors.push(`${label}: ${err instanceof Error ? err.message : String(err)}`)
                    }
                }
                await refreshList(true)
                await refreshUser?.()
                clearSelection()
                toast.success(
                    `Import finished: ${created} created, ${updated} updated, ${skipped} skipped${errors.length ? `, ${errors.length} failed` : ""}.`
                )
                if (errors.length) {
                    toast.error(errors.slice(0, 5).join("; "), { autoClose: 12000 })
                }
            } finally {
                setImportBusy(false)
            }
        },
        [components, refreshList, refreshUser, user]
    )

    return (
        <>
            <div className="h-full flex flex-col min-h-0">
                {/* Header Section */}
                <div className="flex items-center justify-between flex-none p-6 sm:p-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
                            Collection manager
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400">
                            All collections. Open to view data, or edit configuration.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <ShadcnButton
                            variant="outline"
                            onClick={handleReloadComponents}
                            disabled={!user || isRefreshing}
                            className="flex items-center gap-2"
                            aria-label="Reload collections"
                            data-testid="collections-reload-button"
                        >
                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
                            <span>{isRefreshing ? "Refreshing..." : "Reload"}</span>
                        </ShadcnButton>
                        <input
                            ref={importFileRef}
                            type="file"
                            accept=".json,application/json"
                            className="hidden"
                            onChange={handleImportFile}
                        />
                        <ShadcnButton
                            type="button"
                            variant="outline"
                            disabled={!user || importBusy}
                            onClick={() => importFileRef.current?.click()}
                            className="flex items-center gap-2"
                            aria-label="Import collection configs"
                        >
                            <Upload className="w-4 h-4" />
                            <span>{importBusy ? "Importing…" : "Import"}</span>
                        </ShadcnButton>
                        <ShadcnButton
                            type="button"
                            variant="outline"
                            disabled={!user || selectedComponents.length === 0}
                            onClick={handleExportSelected}
                            className="flex items-center gap-2"
                            aria-label="Export selected collection configs"
                        >
                            <Download className="w-4 h-4" />
                            <span>Export selected</span>
                        </ShadcnButton>
                        <ShadcnButton
                            type="button"
                            variant="outline"
                            disabled={!user || components.length === 0}
                            onClick={handleExportAll}
                            className="flex items-center gap-2"
                            aria-label="Export all collection configs"
                        >
                            <Download className="w-4 h-4" />
                            <span>Export all</span>
                        </ShadcnButton>
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <ShadcnButton
                                        onClick={handleNewComponent}
                                        disabled={!canCreateNew}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Plus className="w-5 h-5" />
                                        <span>New Collection</span>
                                    </ShadcnButton>
                                </TooltipTrigger>
                                {!canCreateNew && (
                                    <TooltipContent className="text-base px-4 py-2">
                                        <p>Limit reached</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>

                {components.length > 0 ? (
                    <div className="flex-none px-6 sm:px-8 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                        <div className="relative w-full sm:max-w-sm">
                            <Search className={searchInputOverlayIconClass()} aria-hidden />
                            <Input
                                type="search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search collections by name, slug, UID or creator..."
                                className="pl-9"
                                aria-label="Search collections"
                            />
                        </div>
                    </div>
                ) : null}

                {someRowsSelected ? (
                    <div className="flex-none flex flex-wrap items-center gap-2 px-6 sm:px-8 py-2 border-b border-gray-200 dark:border-gray-700 bg-muted/40 text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium">{selectedUids.size} selected</span>
                        <ShadcnButton type="button" variant="ghost" size="sm" onClick={clearSelection}>
                            Clear
                        </ShadcnButton>
                        <ShadcnButton type="button" variant="secondary" size="sm" onClick={handleExportSelected}>
                            <Download className="w-4 h-4 mr-1" />
                            Export selected
                        </ShadcnButton>
                    </div>
                ) : null}

                {componentsLoading && components.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center p-8">
                        <div className="text-center">
                            <RefreshCw className="w-10 h-10 mx-auto text-gray-400 animate-spin mb-4" />
                            <p className="text-gray-500 dark:text-gray-400">Loading your collections...</p>
                        </div>
                    </div>
                ) : components.length > 0 ? (
                    <div className="flex-1 min-h-0 overflow-y-auto w-full p-6">
                        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-sm overflow-x-auto pb-12">
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent border-gray-200 dark:border-gray-800">
                                        <TableHead className="w-10 pl-3 align-middle">
                                            <Checkbox
                                                checked={allRowsSelected}
                                                onCheckedChange={() => toggleSelectAll()}
                                                aria-label="Select all collections"
                                            />
                                        </TableHead>
                                        <TableHead className="text-gray-900 dark:text-gray-100 min-w-[10rem] max-w-[18rem]">
                                            Collection
                                        </TableHead>
                                        <TableHead className="text-gray-900 dark:text-gray-100 min-w-[7rem]">UID</TableHead>
                                        <TableHead className="text-gray-900 dark:text-gray-100 min-w-[10rem] max-w-[18rem]">
                                            Creator · Quick set
                                        </TableHead>
                                        <TableHead className="text-gray-900 dark:text-gray-100 text-right w-24">Fields</TableHead>
                                        <TableHead className="text-gray-900 dark:text-gray-100 w-28">Features</TableHead>
                                        <TableHead className="text-gray-900 dark:text-gray-100 whitespace-nowrap w-32">Updated</TableHead>
                                        <TableHead className="text-gray-900 dark:text-gray-100 text-right w-[1%]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredComponents.length === 0 ? (
                                        <TableRow className="hover:bg-transparent">
                                            <TableCell colSpan={8} className="text-center py-10 text-gray-500 dark:text-gray-400">
                                                No collections match your search.
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredComponents.map((component: any) => {
                                        const fields = component.fields || []
                                        const fieldCount = fields.length
                                        const quickSetFields = getQuickSetFields(fields)
                                        const previewLabels = fields
                                            .slice(0, 3)
                                            .map((f: any) => f.label || f.name)
                                            .filter(Boolean)
                                            .join(", ")
                                        const extraFields = fieldCount > 3 ? fieldCount - 3 : 0
                                        const showGroupsBadge =
                                            (isAdmin || canViewCollectionAdvanced) && Boolean(component.showGroups)
                                        const updatedRaw =
                                            component.dataSource === "mongodb"
                                                ? component.updated || component.created
                                                : null
                                        const updatedLabel = updatedRaw
                                            ? new Date(updatedRaw).toLocaleDateString()
                                            : "—"
                                        const slugLower = (component?.slug || "").toLowerCase()
                                        const isUsersCollection = slugLower === "users"
                                        const isOwnCollection =
                                            userId != null &&
                                            (component?.createdBy === userId || component?._createdBy === userId)
                                        const canDeleteRow = isAdmin ? !isUsersCollection : isOwnCollection && !isUsersCollection
                                        const creatorOwnerUid =
                                            component.createdBy != null && String(component.createdBy).trim() !== ""
                                                ? String(component.createdBy).trim()
                                                : component._createdBy != null && String(component._createdBy).trim() !== ""
                                                    ? String(component._createdBy).trim()
                                                    : ""
                                        const creatorEmail = component.creatorEmail
                                            ? String(component.creatorEmail).trim()
                                            : ""
                                        const showCreatorEmail =
                                            creatorEmail &&
                                            creatorEmail.toLowerCase() !==
                                            String(component.creatorDisplayName || "").toLowerCase()
                                        const creatorTitle = [
                                            component.creatorDisplayName,
                                            showCreatorEmail ? creatorEmail : null,
                                            creatorOwnerUid,
                                        ]
                                            .filter(Boolean)
                                            .join(" · ")

                                        const quickSetTitle =
                                            quickSetFields.length > 0
                                                ? quickSetFields.map((f: any) => f.label || f.name).join(", ")
                                                : undefined

                                        return (
                                            <ContextMenu key={component.uid}>
                                                <ContextMenuTrigger asChild>
                                                    <TableRow
                                                        className="border-gray-200 dark:border-gray-800 align-top cursor-default"
                                                        onDoubleClick={() => handleClickEditComponent(component)}
                                                    >
                                                        <TableCell
                                                            className="py-4 pl-3 align-top w-10"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onDoubleClick={(e) => e.stopPropagation()}
                                                        >
                                                            <Checkbox
                                                                checked={selectedUids.has(component.uid)}
                                                                onCheckedChange={(c) =>
                                                                    toggleSelectUid(component.uid, c === true)
                                                                }
                                                                aria-label={`Select ${component.name || component.slug}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="py-4 align-top max-w-[18rem]">
                                                            <div className="font-medium text-gray-900 dark:text-gray-100 leading-snug">
                                                                {component.name}
                                                            </div>
                                                            <div className="mt-1.5">
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 text-xs font-normal w-fit"
                                                                >
                                                                    {component.slug}
                                                                </Badge>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell
                                                            className="font-mono text-xs text-gray-600 dark:text-gray-400 max-w-[12rem] truncate py-4 align-top"
                                                            title={component.uid}
                                                        >
                                                            {component.uid}
                                                        </TableCell>
                                                        <TableCell
                                                            className="text-sm text-gray-600 dark:text-gray-400 max-w-[18rem] py-4 align-top"
                                                            title={[creatorTitle, quickSetTitle].filter(Boolean).join(" · ")}
                                                        >
                                                            <div title={creatorTitle}>
                                                                {component.creatorDisplayName ? (
                                                                    <>
                                                                        <span className="line-clamp-2 text-gray-900 dark:text-gray-100">
                                                                            {component.creatorDisplayName}
                                                                        </span>
                                                                        {showCreatorEmail ? (
                                                                            <span className="block text-xs text-muted-foreground mt-0.5 break-all">
                                                                                {creatorEmail}
                                                                            </span>
                                                                        ) : null}
                                                                        {component.creatorIsAdmin ? (
                                                                            <span className="block text-xs text-muted-foreground mt-0.5">
                                                                                Labwick admin
                                                                            </span>
                                                                        ) : null}
                                                                    </>
                                                                ) : creatorOwnerUid ? (
                                                                    <span className="font-mono text-xs text-gray-600 dark:text-gray-400 break-all">
                                                                        {creatorOwnerUid}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted-foreground">—</span>
                                                                )}
                                                            </div>
                                                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                                                                {quickSetFields.length > 0 ? (
                                                                    <span
                                                                        className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2"
                                                                        title={quickSetTitle}
                                                                    >
                                                                        {quickSetFields.map((f: any) => f.label || f.name).join(", ")}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted-foreground text-sm">—</span>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-right align-top py-4">
                                                            <div className="tabular-nums text-gray-900 dark:text-gray-100">{fieldCount}</div>
                                                            {previewLabels ? (
                                                                <div
                                                                    className="text-xs text-muted-foreground mt-1 max-w-[14rem] text-right ml-auto line-clamp-2"
                                                                    title={
                                                                        extraFields > 0
                                                                            ? `${previewLabels} (+${extraFields} more)`
                                                                            : previewLabels
                                                                    }
                                                                >
                                                                    {previewLabels}
                                                                    {extraFields > 0 ? ` (+${extraFields})` : ""}
                                                                </div>
                                                            ) : (
                                                                <div className="text-xs text-muted-foreground mt-1">—</div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="py-4 align-top">
                                                            {showGroupsBadge ? (
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="bg-green-100 text-green-800 dark:bg-green-900/50 text-xs"
                                                                >
                                                                    Groups
                                                                </Badge>
                                                            ) : (
                                                                <span className="text-muted-foreground text-sm">—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap py-4 align-top">
                                                            {updatedLabel}
                                                        </TableCell>
                                                        <TableCell
                                                            className="text-right py-4 align-top"
                                                            onDoubleClick={(e) => e.stopPropagation()}
                                                        >
                                                            <div className="flex items-center justify-end gap-1 flex-nowrap">
                                                                <ShadcnButton
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    aria-label="Open collection"
                                                                    onClick={() => handleOpenCollection(component)}
                                                                    className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 h-8 w-8"
                                                                >
                                                                    <ExternalLink className="w-4 h-4" />
                                                                </ShadcnButton>
                                                                <ShadcnButton
                                                                    variant="outline"
                                                                    size="sm"
                                                                    aria-label="Edit"
                                                                    onClick={() => handleClickEditComponent(component)}
                                                                    className="h-8 px-2 text-gray-600 dark:text-gray-300"
                                                                >
                                                                    Edit
                                                                </ShadcnButton>
                                                                {canDeleteRow ? (
                                                                    <ShadcnButton
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        aria-label="Delete collection"
                                                                        onClick={() => handleDeleteClick(component)}
                                                                        className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 h-8 w-8"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </ShadcnButton>
                                                                ) : null}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                </ContextMenuTrigger>
                                                <ContextMenuContent className="min-w-[12rem]">
                                                    <ContextMenuItem
                                                        onSelect={() => handleOpenCollection(component)}
                                                    >
                                                        Open collection
                                                    </ContextMenuItem>
                                                    <ContextMenuItem
                                                        onSelect={() => handleClickEditComponent(component)}
                                                    >
                                                        Edit configuration
                                                    </ContextMenuItem>
                                                    <ContextMenuItem
                                                        onSelect={() => handleExportOne(component)}
                                                    >
                                                        Export config
                                                    </ContextMenuItem>
                                                    {someRowsSelected ? (
                                                        <ContextMenuItem onSelect={() => handleExportSelected()}>
                                                            Export selected ({selectedUids.size})
                                                        </ContextMenuItem>
                                                    ) : null}
                                                    <ContextMenuSeparator />
                                                    <ContextMenuItem onSelect={() => handleExportAll()}>
                                                        Export all collections
                                                    </ContextMenuItem>
                                                    <ContextMenuItem
                                                        onSelect={() => importFileRef.current?.click()}
                                                    >
                                                        Import configs…
                                                    </ContextMenuItem>
                                                    {canDeleteRow ? (
                                                        <>
                                                            <ContextMenuSeparator />
                                                            <ContextMenuItem
                                                                className="text-destructive focus:text-destructive"
                                                                onSelect={() => handleDeleteClick(component)}
                                                            >
                                                                Delete collection
                                                            </ContextMenuItem>
                                                        </>
                                                    ) : null}
                                                </ContextMenuContent>
                                            </ContextMenu>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-900/50 dark:to-gray-900">
                        <div className="max-w-md w-full px-6 py-12 text-center">
                            <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center ring-4 ring-blue-100 dark:ring-blue-900/30">
                                <Database className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No collections yet</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8">
                                Create a collection to define a data structure and start adding records.
                            </p>
                            <TooltipProvider delayDuration={0}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <ShadcnButton
                                            onClick={handleNewComponent}
                                            disabled={!canCreateNew}
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 mx-auto text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Create Collection</span>
                                        </ShadcnButton>
                                    </TooltipTrigger>
                                    {!canCreateNew && (
                                        <TooltipContent className="text-base px-4 py-2">
                                            <p>Limit reached</p>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                )}
            </div>

            {showNewDrawer && (
                <Dialog open={showNewDrawer} onOpenChange={(open) => {
                    if (!open) {
                        if (allowCloseWithoutConfirmRef.current) {
                            allowCloseWithoutConfirmRef.current = false;
                            setCollectionWizardUi(null);
                            setShowNewDrawer(false);
                            setTimeout(() => {
                                cleanupOverlays();
                            }, 50);
                            return;
                        }
                        if (editComponentRef.current?.hasInteracted()) {
                            setShowCancelConfirm(true);
                        } else {
                            setCollectionWizardUi(null);
                            setShowNewDrawer(false);
                            // Cleanup overlays when dialog closes
                            setTimeout(() => {
                                cleanupOverlays();
                            }, 50);
                        }
                    }
                }}>
                    <DialogContent
                        className="!fixed !left-[1vw] !right-[1vw] !top-[6vh] !bottom-[6vh] !m-0 !h-auto !max-h-none !w-auto !max-w-none !p-0 !gap-0 !flex !flex-col !overflow-hidden !translate-x-0 !translate-y-0 [&>button]:hidden"
                    >
                        {/* Header */}
                        <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
                            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900 m-0 truncate">
                                        {isNew
                                            ? collectionWizardUi
                                                ? `Create collection · Step ${collectionWizardUi.step} of ${collectionWizardUi.totalSteps}`
                                                : "Create collection"
                                            : componentToEdit?.name
                                                ? `Edit ${componentToEdit.name}`
                                                : "Edit collection"}
                                    </DialogTitle>
                                </div>
                            </div>
                        </DialogHeader>

                        {/* Scrollable Content */}
                        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain edit-dialog-scrollable p-5 sm:p-7 bg-gray-200 dark:bg-gray-950">
                            <EditComponent
                                key={isNew ? `new-${newEditorNonce}` : componentToEdit?.uid || "edit"}
                                ref={editComponentRef}
                                onEditComponent={handleOnEditComponent}
                                onNewComponentCreated={handleNewComponentCreated}
                                hideActionButtons={true}
                                onCollectionWizardUiChange={setCollectionWizardUi}
                            />
                        </div>

                        <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                            {isNew && collectionWizardUi?.showBack ? (
                                <ShadcnButton
                                    type="button"
                                    variant="outline"
                                    onClick={() => editComponentRef.current?.wizardBack?.()}
                                    className="px-4 py-2"
                                >
                                    Back
                                </ShadcnButton>
                            ) : null}
                            <ShadcnButton variant="outline" onClick={handleCancelClick} className="px-4 py-2">
                                Cancel
                            </ShadcnButton>
                            <ShadcnButton
                                disabled={Boolean(isNew && collectionWizardUi?.nextDisabled)}
                                title={
                                    isNew && collectionWizardUi?.nextDisabledReason
                                        ? collectionWizardUi.nextDisabledReason
                                        : undefined
                                }
                                onClick={async () => {
                                    if (editComponentRef.current?.save) {
                                        const ok = await editComponentRef.current.save()
                                        if (ok) await handleSavedOrCreated()
                                    }
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {isNew
                                    ? collectionWizardUi?.primaryLabel ?? "Next"
                                    : componentToEdit?.name
                                        ? "Save changes"
                                        : "Save"}
                            </ShadcnButton>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {/* Cancel Confirmation Dialog */}
            <AlertDialog open={showCancelConfirm} onOpenChange={(open) => {
                setShowCancelConfirm(open);
                if (!open) {
                    // Cleanup overlays when alert dialog closes
                    setTimeout(() => {
                        cleanupOverlays();
                    }, 50);
                }
            }}>
                <AlertDialogContent className="sm:max-w-[425px] mx-4">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <span>Discard Changes?</span>
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-sm text-gray-600">
                            Are you sure you want to close this form? Any unsaved changes will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
                        <AlertDialogCancel className="w-full sm:w-auto">
                            Continue Editing
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmCancel}
                            className="w-full sm:w-auto bg-orange-600 hover:bg-red-700"
                        >
                            Discard Changes
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <MuiDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} aria-labelledby="delete-collection-dialog">
                <MuiDialogTitle>{componentToDelete?.name || "Collection"}</MuiDialogTitle>
                <MuiDialogContent>
                    <DialogContentText>Are you sure you want to delete this collection?</DialogContentText>
                </MuiDialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} disabled={deleteInProgress}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" disabled={deleteInProgress}>
                        {deleteInProgress ? <PulseLoader size={8} /> : "Delete"}
                    </Button>
                </DialogActions>
            </MuiDialog>
        </>
    )
}

export default Components

